/**
 * workflowMode — client-side mirror of /app/backend/workflow_mode.py.
 *
 * The Visit lifecycle stays constant internally. This module decides which
 * ACTIONS are visible for a given (mode × role × current status) so every
 * surface (Calendar, Waiting Room, Chairside, Patient Profile) exposes a
 * consistent, minimum-viable set of controls.
 *
 * Rules of the road:
 *   1. Never read `appointment.status` or `queue.current_status` directly
 *      from a UI component — always ask `computeVisitStatus(appt, queue)`.
 *   2. Never hard-code a role → button mapping in a page — always ask
 *      `visibleActions(mode, role, status)`.
 *   3. Adding a new mode requires editing ONLY this file + the backend
 *      workflow_mode.py mirror. No page changes.
 */

export const MODES = /** @type {const} */ (["solo", "reception", "multi_doctor"]);

/** Human-readable labels + one-line description used in Settings UI. */
export const MODE_META = {
  solo: {
    label: "Solo Practice",
    description:
      "One button opens the visit, cascades check-in + waiting, and drops you at the chair.",
    idealFor: "Solo dentists · single chair · no receptionist",
  },
  reception: {
    label: "Reception Assisted",
    description:
      "Reception checks patients in and manages the waiting room. Doctors pick up from In Chair.",
    idealFor: "1–3 doctors · dedicated receptionist",
  },
  multi_doctor: {
    label: "Multi-Doctor / Enterprise",
    description:
      "Full workflow — every transition is explicit, transfers logged, roles enforced.",
    idealFor: "3+ doctors · multiple chairs · multi-location chains",
  },
};

/** Canonical Visit status values (must match backend workflow_mode.py). */
export const VISIT_STATUSES = /** @type {const} */ ([
  "scheduled",
  "checked_in",
  "waiting",
  "in_chair",
  "clinical_complete",
  "billing",
  "completed",
  "no_show",
  "walked_out",
  "cancelled",
]);

/**
 * Compute the canonical visit_status from persistent state.
 * Mirrors backend/workflow_mode.py:compute_visit_status.
 *
 * @param {object|null} appointment
 * @param {object|null} queueEntry
 * @returns {string}
 */
export function computeVisitStatus(appointment, queueEntry) {
  if (!appointment && !queueEntry) return "scheduled";
  const q = queueEntry?.current_status;
  const a = appointment?.status;
  if (q === "left") return "walked_out";
  if (q === "done") return "clinical_complete";
  if (q === "in_chair") return "in_chair";
  if (q === "waiting") return "waiting";
  if (q === "checked_in") return "checked_in";
  if (q === "ready") return "waiting";
  if (a === "cancelled") return "cancelled";
  if (a === "no_show") return "no_show";
  if (a === "completed") return "completed";
  if (a === "checked_in") return "checked_in";
  if (a === "in_chair") return "in_chair";
  return "scheduled";
}

/** Compact display metadata for the current status. */
export const STATUS_META = {
  scheduled:         { label: "Scheduled",         color: "#8A3F1E", bg: "#FDF3ED" },
  checked_in:        { label: "Checked In",        color: "#1F4B66", bg: "#E4EFF5" },
  waiting:           { label: "Waiting",           color: "#7A5A20", bg: "#F5EFE0" },
  in_chair:          { label: "In Chair",          color: "#264029", bg: "#E0EBDE" },
  clinical_complete: { label: "Clinical Complete", color: "#2D6A4F", bg: "#E8F0EB" },
  billing:           { label: "Billing",           color: "#4A2148", bg: "#F0E7EE" },
  completed:         { label: "Completed",         color: "#2D6A4F", bg: "#E8F0EB" },
  no_show:           { label: "No Show",           color: "#B94A48", bg: "#FCECEC" },
  walked_out:        { label: "Walked Out",        color: "#B94A48", bg: "#FCECEC" },
  cancelled:         { label: "Cancelled",         color: "#6B7280", bg: "#EDEDED" },
};

/**
 * Return the set of action-keys that should be exposed as clickable buttons
 * for the current (mode, role, status) triple.
 *
 * Action keys — the button component knows how to render each:
 *   "start_visit"  — cascading one-tap (creates queue + goes straight to in_chair)
 *   "check_in"     — reception check-in only (queue in `waiting`)
 *   "assign_chair" — reception assigns a chair
 *   "mark_in_chair"— doctor pulls patient into chair (waiting → in_chair)
 *   "complete"     — mark clinical work done (in_chair → clinical_complete)
 *   "open_chairside"— navigate to the chairside workspace
 *   "open_billing" — jump to the invoice wizard
 *   "cancel"       — cancel/no-show/walked-out
 *
 * @param {string} mode         one of MODES
 * @param {string} role         admin | doctor | receptionist
 * @param {string} status       one of VISIT_STATUSES
 * @returns {string[]}
 */
export function visibleActions(mode, role, status) {
  const isSolo = mode === "solo";
  const isReception = mode === "reception";
  const isMulti = mode === "multi_doctor";
  const isDoctor = role === "doctor" || role === "admin";
  const isReceptionRole = role === "receptionist" || role === "admin";

  const out = [];

  // ---- Scheduled ---------------------------------------------------------
  if (status === "scheduled") {
    if (isSolo && isDoctor) {
      out.push("start_visit");                       // one-tap
    } else if (isReception) {
      if (isReceptionRole) out.push("check_in");
      // Doctors in reception mode wait for reception to check the patient in.
      if (isDoctor && !isReceptionRole) {
        // Rare — doctors CAN start visits directly if reception isn't around.
        // Show as a secondary escape hatch.
        out.push("start_visit");
      }
    } else if (isMulti) {
      if (isReceptionRole) out.push("check_in");
    }
    out.push("cancel");
    return out;
  }

  // ---- Checked-in / Waiting ---------------------------------------------
  if (status === "checked_in" || status === "waiting") {
    if (isSolo && isDoctor) {
      out.push("mark_in_chair");                     // solo skips waiting-room UI
    } else if (isReception) {
      if (isReceptionRole) out.push("assign_chair");
      if (isDoctor) out.push("mark_in_chair");
    } else if (isMulti) {
      if (isReceptionRole) out.push("assign_chair");
      if (isDoctor) out.push("mark_in_chair");
    }
    out.push("cancel");
    return out;
  }

  // ---- In chair ----------------------------------------------------------
  if (status === "in_chair") {
    if (isDoctor) {
      out.push("open_chairside");
      out.push("complete");
    }
    if (isReceptionRole && !isSolo) {
      // Reception can peek at the chair status even mid-visit.
      out.push("open_chairside");
    }
    return out;
  }

  // ---- Clinical complete / Billing --------------------------------------
  if (status === "clinical_complete" || status === "billing") {
    if (isReceptionRole || (isSolo && isDoctor)) out.push("open_billing");
    return out;
  }

  // ---- Terminal states -----
  return [];
}

/**
 * Convenience: extract the effective workflow_mode from a clinic doc.
 *
 * The backend always returns `settings.workflow_mode` populated (either the
 * explicit admin choice or the auto-suggested default).
 */
export function modeFromClinic(clinic) {
  const m = clinic?.settings?.workflow_mode;
  return MODES.includes(m) ? m : "reception";
}
